import L from 'leaflet';
import { useCallback, useMemo } from 'react';
import { projectWithEta } from 'features/Responder/features/Tours/utils';
import hasLocation from 'features/Responder/features/Tours/features/TourMap/utils/hasLocation';
import { buildPinIcon } from 'features/Responder/features/ResponderMap/utils/pinIcon';

/**
 * Encapsulate checkpoint map interactions: snapping, ETA, icon setup.
 */
export function useCheckpointActions({
	routePoints,
	routeMetrics,
	totalDurationSeconds,
	cumulativeEtaSeconds,
	metersPerDegreeLat,
	metersPerDegreeLng,
	checkpoints,
	stops,
	addCheckpoint,
	updateCheckpoint,
	setSelectedCheckpointId,
	snapEnabled,
	googleApiKey,
	startLocation,
	setStartLocation,
	userPosition,
	setUserPosition,
	setHasGeolocated,
	setMapZoom,
	suppressFocusNext,
	suppressAutoFitNext,
	mapRef,
	markerColor = "#2e7be7",
}) {
	const startIcon = useMemo(
		() =>
			buildPinIcon("#d9534f"),
		[]
	);
	const stopIcon = useMemo(
		() =>
			buildPinIcon("#2e7be7"),
		[]
	);
	const handleIcon = useMemo(
		() =>
			L.divIcon({
				className: 'checkpoint-handle-icon',
				html: '<div class="checkpoint-handle-dot"></div>',
				iconSize: [16, 16],
				iconAnchor: [8, 8],
			}),
		[]
	);
	const checkpointIcon = useMemo(() => buildPinIcon(markerColor), [markerColor]);
	const personnelOnlineIcon = useMemo(
		() =>
			buildPinIcon("#27ae60"),
		[]
	);
	const personnelOfflineIcon = useMemo(
		() =>
			buildPinIcon("#7f8c8d"),
		[]
	);

	const snapToRoad = useCallback(
		async (latlng) => {
			if (!snapEnabled) return { lat: latlng.lat, lng: latlng.lng };
			if (!googleApiKey) return latlng;
			try {
				const resp = await fetch(
					`https://roads.googleapis.com/v1/nearestRoads?points=${latlng.lat},${latlng.lng}&key=${googleApiKey}`
				);
				if (!resp.ok) return latlng;
				const data = await resp.json();
				const snapped = data?.snappedPoints?.[0]?.location;
				if (snapped?.latitude != null && snapped?.longitude != null) {
					return { lat: snapped.latitude, lng: snapped.longitude };
				}
			} catch {
				// ignore snapping errors, keep original position
			}
			return { lat: latlng.lat, lng: latlng.lng };
		},
		[googleApiKey, snapEnabled]
	);

	const nearestStopIndex = useCallback(
		(lat, lng) => {
			let best = 1;
			let bestDist = Infinity;
			stops.forEach((s, idx) => {
				const ll = {
					lat: Number(s.lat ?? s.latitude ?? s.coordinates?.[0] ?? s[0]),
					lng: Number(s.lng ?? s.longitude ?? s.coordinates?.[1] ?? s[1]),
				};
				if (!Number.isFinite(ll.lat) || !Number.isFinite(ll.lng)) return;
				const dLat = ll.lat - lat;
				const dLng = ll.lng - lng;
				const dist = dLat * dLat + dLng * dLng;
				if (dist < bestDist) {
					bestDist = dist;
					best = idx + 1;
				}
			});
			return best;
		},
		[stops]
	);

	const computeEtaFor = useCallback(
		(latlng, legStartsMeters) => {
			const stopIndexNearest = nearestStopIndex(latlng.lat, latlng.lng);
			if (
				routePoints.length > 1 &&
				routeMetrics.total > 0 &&
				totalDurationSeconds != null
			) {
				const { along: clampedAlong, etaSeconds } = projectWithEta(
					routePoints,
					routeMetrics,
					totalDurationSeconds,
					latlng
				);
				const stopIndex = legStartsMeters.length
					? (() => {
							for (let i = 0; i < legStartsMeters.length - 1; i++) {
								if (
									clampedAlong >= legStartsMeters[i] &&
									clampedAlong <= legStartsMeters[i + 1]
								) {
									return i + 1;
								}
							}
							return legStartsMeters.length - 1 > 0
								? legStartsMeters.length - 1
								: stopIndexNearest || 1;
					  })()
					: stopIndexNearest || 1;
				return { stopIndex, etaSeconds, along: clampedAlong };
			}
			const etaSeconds =
				stopIndexNearest > 0 && cumulativeEtaSeconds[stopIndexNearest - 1] != null
					? cumulativeEtaSeconds[stopIndexNearest - 1]
					: null;
			return { stopIndex: stopIndexNearest || 1, etaSeconds, along: null };
		},
		[cumulativeEtaSeconds, nearestStopIndex, routeMetrics, routePoints, totalDurationSeconds]
	);

	const addCheckpointAt = useCallback(
		async (latlng, geometryType = 'circle', legStartsMeters = []) => {
			const positioned = await snapToRoad(latlng);
			const { stopIndex, etaSeconds } = computeEtaFor(positioned, legStartsMeters);
			const label = `Checkpoint ${String.fromCharCode(65 + checkpoints.length)}`;
			const seedPolygon = (lat, lng) => {
				const delta = 0.00022; // ~24m at equator; renders a small square footprint.
				return [
					{ lat: lat + delta, lng: lng + delta },
					{ lat: lat + delta, lng: lng - delta },
					{ lat: lat - delta, lng: lng - delta },
					{ lat: lat - delta, lng: lng + delta },
				];
			};
			const buildGeometry = () => {
				const type = geometryType || 'circle';
				if (type === 'polygon' || type === 'zone') {
					return {
						type,
						points: seedPolygon(positioned.lat, positioned.lng),
					};
				}
				return {
					type,
					radiusMeters: 75,
				};
			};

			if (!hasLocation(startLocation)) {
				const fromUserPos = Array.isArray(userPosition)
					? { lat: userPosition[0], lng: userPosition[1] }
					: userPosition && userPosition.lat != null && userPosition.lng != null
					? userPosition
					: null;

				if (fromUserPos) {
					setStartLocation((prev) => ({
						...prev,
						lat: fromUserPos.lat,
						lng: fromUserPos.lng,
						address: prev.address || 'Current location',
						useCurrent: true,
						zoneId: null,
						geometry: null,
					}));
				} else if (navigator?.geolocation) {
					navigator.geolocation.getCurrentPosition(
						(pos) => {
							setStartLocation((prev) => ({
								...prev,
								lat: pos.coords.latitude,
								lng: pos.coords.longitude,
								address: prev.address || 'Current location',
								useCurrent: true,
								zoneId: null,
								geometry: null,
							}));
						},
						() => {
							setStartLocation((prev) => ({
								...prev,
								lat: positioned.lat,
								lng: positioned.lng,
								address: prev.address || 'Start',
								useCurrent: false,
								zoneId: null,
								geometry: null,
							}));
						}
					);
				} else {
					setStartLocation((prev) => ({
						...prev,
						lat: positioned.lat,
						lng: positioned.lng,
						address: prev.address || 'Start',
						useCurrent: false,
						zoneId: null,
						geometry: null,
					}));
				}
			}
			const lastCheckpoint =
				Array.isArray(checkpoints) && checkpoints.length
					? checkpoints[checkpoints.length - 1]
					: null;
			const lastHasLocation = lastCheckpoint ? hasLocation(lastCheckpoint) : false;
			const fallbackMode =
				lastCheckpoint?.mode ||
				lastCheckpoint?.travelMode ||
				(checkpoints.length > 1
					? checkpoints[checkpoints.length - 2]?.mode ||
					  checkpoints[checkpoints.length - 2]?.travelMode
					: null) ||
				'DRIVING';

			if (lastCheckpoint && !lastHasLocation) {
				if (suppressAutoFitNext) {
					suppressAutoFitNext.current = true;
				}
				updateCheckpoint(lastCheckpoint.id, {
					lat: positioned.lat,
					lng: positioned.lng,
					address: lastCheckpoint.address || '',
					placeId: '',
					radiusMeters: lastCheckpoint.radiusMeters || 75,
					etaSeconds,
					stopIndex:
						lastCheckpoint.stopIndex != null
							? lastCheckpoint.stopIndex
							: stopIndex ?? checkpoints.length,
					timeWindowSeconds: lastCheckpoint.timeWindowSeconds ?? 0,
					label: lastCheckpoint.label || label,
					mode: fallbackMode,
					travelMode: fallbackMode,
					geometry: {
						...(lastCheckpoint.geometry || {}),
						...(buildGeometry() || {}),
					},
				});
				suppressFocusNext.current = true;
				setSelectedCheckpointId(lastCheckpoint.id);
			} else {
				if (suppressAutoFitNext) {
					suppressAutoFitNext.current = true;
				}
				const id = addCheckpoint({
					lat: positioned.lat,
					lng: positioned.lng,
					radiusMeters: 75,
					etaSeconds,
					stopIndex: stopIndex ?? checkpoints.length + 1,
					timeWindowSeconds: 0,
					label,
					mode: fallbackMode,
					travelMode: fallbackMode,
					geometry: buildGeometry(),
				});
				if (id) {
					suppressFocusNext.current = true;
					setSelectedCheckpointId(id);
				}
			}
		},
		[
			addCheckpoint,
			checkpoints.length,
			computeEtaFor,
			checkpoints,
			setSelectedCheckpointId,
			setStartLocation,
			snapToRoad,
			startLocation,
			suppressFocusNext,
			suppressAutoFitNext,
			userPosition,
		]
	);

	const addStartAt = useCallback(
		async (latlng) => {
			const positioned = await snapToRoad(latlng);
			setHasGeolocated(true);
			setUserPosition([positioned.lat, positioned.lng]);
			const targetZoom = (() => {
				try {
					const currentZoom = mapRef.current?.getZoom?.() ?? 10;
					return Math.min(Math.max(currentZoom, 15), 17);
				} catch {
					return 16;
				}
			})();
			setMapZoom(targetZoom);
			setStartLocation((prev) => ({
				...prev,
				lat: positioned.lat,
				lng: positioned.lng,
				address: prev.address || 'Dropped start',
				placeId: '',
				useCurrent: true,
				zoneId: null,
				geometry: null,
			}));
			try {
				const map = mapRef.current;
				if (map) {
					map.setView([positioned.lat, positioned.lng], targetZoom, {
						animate: true,
					});
				}
			} catch (err) {
				console.warn('center on start failed', err);
			}
		},
		[mapRef, setHasGeolocated, setMapZoom, setStartLocation, setUserPosition, snapToRoad]
	);

	return {
		startIcon,
		stopIcon,
		handleIcon,
		checkpointIcon,
		personnelOnlineIcon,
		personnelOfflineIcon,
		computeEtaFor,
		addCheckpointAt,
		addStartAt,
		snapToRoad,
	};
}


